import React, { useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RangeSlider from 'Components/UI/RangeSlider';
import {
  useBulkCommunicationFilterDispatchContext,
  useBulkCommunicationFilterStateContext,
} from 'Components/Modals/BulkCommunicationDialog/BulkCommunicationFilterContext';
import { applyFilters } from 'Components/Modals/BulkCommunicationDialog/BulkCommunicationFilterActions';
import FilterGroup from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/components/FilterGroup';
import FilterGroupSection from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/components/FilterGroup/components/FilterGroupSection';
import FilterGroupRow from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/components/FilterGroup/components/FilterGroupRow';
import FilterGroupColumn from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/components/FilterGroup/components/FilterGroupColumn';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';

import Spacer from 'Components/UI/Spacer';
import MetaPickList from 'Components/AdvisorApp/Viewport/NotificationCenter/MetaPickList';
import TextField from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/TextField';
import Switch from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Switch';
import YesNoToggleButtons from 'Components/UI/YesNoToggleButtons';
import BulkCommunicationSelectedFilters from 'Components/Modals/BulkCommunicationDialog/components/BulkCommunicationFilters/components/BulkCommunicationSelectedFilters';
import DateField from 'Components/UI/DateField';
import SelectControl from 'Components/UI/SelectControl';
import InputAdornment from '@material-ui/core/InputAdornment';
import PropTypes from 'prop-types';
import Autocomplete from 'Components/UI/Autocomplete';

const useStyles = makeStyles((theme) => ({
  root: {
  },
  row: {
    display: 'flex',
  },
  textField: {
    width: '100%',
  },
  group: {
    width: 200,
    marginRight: theme.spacing(2),
  },
  groupContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
  },
  card: {
    width: 275,
  },
  cardContainer: {
    display: 'inline-flex',
    gap: `${theme.spacing(1)}px`,
    flexWrap: 'wrap',
  },
  headerRow: {
    display: 'flex',
  },
  divider: {
    borderLeft: `1px solid ${theme.palette.facet.mediumGray}`,
    margin: `0 ${theme.spacing(1)}px`,
    height: '2em',
  },
  selectToggle: {
    cursor: 'pointer',
  },
  text: {
    padding: theme.spacing(0.75),
  },
  filterGroupsContainer: {
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  nextMeetingFilter: {
    padding: 0,
  },
  marginBelow: {
    marginBottom: theme.spacing(1.5),
  },
  autocomplete: {
    width: '100%',
  },
  switch: {
    whiteSpace: 'noWrap',
  },
}));

const BulkCommunicationFilters = ({
  displayNeedsPlanningMeetingFilter, // Upgrade or when going into Beta and remove when into default
  linkUrl,
  queryRel,
  onApplyFilters,
  resource,
}) => {
  const [selectedFilterGroups, setSelectedFilterGroups] = useState([]);
  const classes = useStyles();
  const { logEvent } = useEventTrackingLogEvent();

  const { pendingFilters } = useBulkCommunicationFilterStateContext();
  const filterDispatch = useBulkCommunicationFilterDispatchContext();


  const handleApplyFilters = () => {
    const refreshLink = applyFilters(pendingFilters, resource, linkUrl, onApplyFilters);
    filterDispatch({ type: 'applyFilters' });
    // Apex already uses refreshLink, so we'll just use it again here since it doesn't count against us
    logEvent('Bulk Communication: Clicked Apply Filters Button', { refreshLink });
  };

  // Meta option helpers
  const getMetaOptionMin = field => resource?.meta().query(queryRel).field(field).attr('min');
  const getMetaOptionMax = field => resource?.meta().query(queryRel).field(field).attr('max');
  const getMetaOptionDefault = field => resource?.meta().query(queryRel).field(field).attr('default');

  const addSelectedFilterGroup = group => {
    if (!selectedFilterGroups.includes(group)) {
      setSelectedFilterGroups([...selectedFilterGroups, group]);
    }
  };

  return (
    <div>
      <div className={classes.filterGroupsContainer}>
        <FilterGroup
          id={'filter-group-household'}
          groupName="Household"
          className={classes.group}
          test="bulk-communication-filters"
          onApply={handleApplyFilters}
          ApplyFiltersButtonProps={{
            disabled: !selectedFilterGroups.includes('household'),
          }}
        >
          <FilterGroupColumn>
            <FilterGroupSection
              className={classNames(classes.nextMeetingFilter, classes.marginBelow)}
              label="Next Upcoming Meeting"
            >
              <SelectControl
                options={resource.meta().query(queryRel).field('householdDaysUntilNextMeeting').options()
                  .list()
                  .map(el => ({
                    value: el.id(),
                    name: el.attr('prompt'),
                  }))}
                className={classes.nextMeetingFilter}
                autoWidth
                fullWidth
                onChange={(evt) => {
                  filterDispatch({ type: 'setFilter', name: 'householdDaysUntilNextMeeting', value: evt.target.value });
                  addSelectedFilterGroup('household');
                }}
                value={pendingFilters.householdDaysUntilNextMeeting}
              />
            </FilterGroupSection>
            <FilterGroupSection
              className={classes.nextMeetingFilter}
              label="Time Since Last Meeting"
            >
              <SelectControl
                options={resource.meta().query(queryRel).field('householdTimeSinceLastMeeting').options()
                  .list()
                  .map(el => ({
                    value: el.id(),
                    name: el.attr('prompt'),
                  }))}
                className={classes.nextMeetingFilter}
                autoWidth
                fullWidth
                onChange={(evt) => {
                  filterDispatch({ type: 'setFilter', name: 'householdTimeSinceLastMeeting', value: evt.target.value });
                  addSelectedFilterGroup('household');
                }}
                value={pendingFilters.householdTimeSinceLastMeeting}
              />
            </FilterGroupSection>
          </FilterGroupColumn>
          <FilterGroupRow>
            <FilterGroupColumn>
              <RangeSlider
                label="Service Level"
                minBound={Number(getMetaOptionMin('householdServiceLevelMin'))}
                maxBound={Number(getMetaOptionMax('householdServiceLevelMax'))}
                minValue={Number(pendingFilters.householdServiceLevelMin || getMetaOptionDefault('householdServiceLevelMin'))}
                maxValue={Number(pendingFilters.householdServiceLevelMax || getMetaOptionDefault('householdServiceLevelMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'householdServiceLevelMin', value: v });
                  addSelectedFilterGroup('household');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'householdServiceLevelMax', value: v });
                  addSelectedFilterGroup('household');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      {'$'}
                    </InputAdornment>),
                }}
              />
              <RangeSlider
                label="Total Household AUM"
                minBound={Number(getMetaOptionMin('householdTotalAssetsUnderManagementMin'))}
                maxBound={Number(getMetaOptionMax('householdTotalAssetsUnderManagementMax'))}
                minValue={Number(pendingFilters.householdTotalAssetsUnderManagementMin || getMetaOptionDefault('householdTotalAssetsUnderManagementMin'))}
                maxValue={Number(pendingFilters.householdTotalAssetsUnderManagementMax || getMetaOptionDefault('householdTotalAssetsUnderManagementMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'householdTotalAssetsUnderManagementMin', value: v });
                  addSelectedFilterGroup('household');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'householdTotalAssetsUnderManagementMax', value: v });
                  addSelectedFilterGroup('household');
                }}
              />
              <FilterGroupSection label="Sign Date">
                <DateField
                  label="Min"
                  value={pendingFilters.householdSignDateMin}
                  onChange={(v) => {
                    if (v) {
                      filterDispatch({ type: 'setFilter', name: 'householdSignDateMin', value: v });
                    } else {
                      filterDispatch({ type: 'removeFilter', name: 'householdSignDateMin' });
                    }
                    addSelectedFilterGroup('household');
                  }}
                />
                <DateField
                  label="Max"
                  value={pendingFilters.householdSignDateMax}
                  onChange={(v) => {
                    if (v) {
                      filterDispatch({ type: 'setFilter', name: 'householdSignDateMax', value: v });
                    } else {
                      filterDispatch({ type: 'removeFilter', name: 'householdSignDateMax' });
                    }
                    addSelectedFilterGroup('household');
                  }}
                />
              </FilterGroupSection>
            </FilterGroupColumn>
            <FilterGroupColumn>
              <Switch
                className={classes.switch}
                label="Is Escalated"
                value={!!pendingFilters.householdIsEscalated}
                test="bulk-communication-filters-switch-is-escalated"
                onChange={(v) => {
                  if (v) {
                    filterDispatch({ type: 'setFilter', name: 'householdIsEscalated', value: v });
                  } else {
                    filterDispatch({ type: 'removeFilter', name: 'householdIsEscalated' });
                  }
                  addSelectedFilterGroup('household');
                }}
              />
              {displayNeedsPlanningMeetingFilter && (
                <Switch
                  className={classes.switch}
                  label="Needs Planning Meeting"
                  value={!!pendingFilters.householdNeedsPlanning}
                  test="bulk-communication-filters-switch-needs-planning-meeting"
                  onChange={(v) => {
                    if (v) {
                      filterDispatch({ type: 'setFilter', name: 'householdNeedsPlanning', value: v });
                    } else {
                      filterDispatch({ type: 'removeFilter', name: 'householdNeedsPlanning' });
                    }
                    addSelectedFilterGroup('household');
                  }}
                />
              )}
              <FilterGroupSection label="Best Next Steps">
                <MetaPickList
                  handleSelect={(v) => {
                    filterDispatch({ type: 'setFilter', name: 'householdBestNextSteps', value: v.includes(null) ? [] : v });
                    addSelectedFilterGroup('household');
                  }}
                  options={resource.meta().queries().head()?.field('householdBestNextSteps')
                    .options()
                    .list()
                    .map(el => el._attributeMap)}
                  value={pendingFilters.householdBestNextSteps}
                  allowMultiple
                  fullWidth
                />
              </FilterGroupSection>
            </FilterGroupColumn>
          </FilterGroupRow>
        </FilterGroup>
        <FilterGroup
          id={'filter-group-family-information'}
          groupName="Family Information"
          className={classes.group}
          test="bulk-communication-filters"
          onApply={handleApplyFilters}
          ApplyFiltersButtonProps={{
            disabled: !selectedFilterGroups.includes('familyInfo'),
          }}
        >
          <FilterGroupColumn>
            <FilterGroupSection label="Marital Status">
              <MetaPickList
                handleSelect={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'householdMaritalStatus', value: v.includes(null) ? [] : v });
                  addSelectedFilterGroup('familyInfo');
                }}
                options={resource.meta().queries().head()?.field('householdMaritalStatus')
                  .options()
                  .list()
                  .map(el => el._attributeMap)}
                value={pendingFilters.householdMaritalStatus}
                allowMultiple
                fullWidth
              />
            </FilterGroupSection>
          </FilterGroupColumn>
          <FilterGroupRow>
            <FilterGroupColumn>
              <RangeSlider
                test={'filter-client-age'}
                label="Client Age"
                minBound={Number(getMetaOptionMin('clientAgeMin'))}
                maxBound={Number(getMetaOptionMax('clientAgeMax'))}
                minValue={Number(pendingFilters.clientAgeMin || getMetaOptionDefault('clientAgeMin'))}
                maxValue={Number(pendingFilters.clientAgeMax || getMetaOptionDefault('clientAgeMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'clientAgeMin', value: v });
                  addSelectedFilterGroup('familyInfo');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'clientAgeMax', value: v });
                  addSelectedFilterGroup('familyInfo');
                }}
              />
              <RangeSlider
                label="Spouse Age"
                minBound={Number(getMetaOptionMin('spouseAgeMin'))}
                maxBound={Number(getMetaOptionMax('spouseAgeMax'))}
                minValue={Number(pendingFilters.spouseAgeMin || getMetaOptionDefault('spouseAgeMin'))}
                maxValue={Number(pendingFilters.spouseAgeMax || getMetaOptionDefault('spouseAgeMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'spouseAgeMin', value: v });
                  addSelectedFilterGroup('familyInfo');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'spouseAgeMax', value: v });
                  addSelectedFilterGroup('familyInfo');
                }}
              />
            </FilterGroupColumn>
            <FilterGroupColumn>
              <FilterGroupSection label="Has Dependents/Children">
                <YesNoToggleButtons
                  value={pendingFilters.householdHasDependents}
                  onChange={(v) => {
                    if (v === null) {
                      filterDispatch({ type: 'removeFilter', name: 'householdHasDependents' });
                    } else if (v === false) {
                      filterDispatch({ type: 'setFilter', name: 'householdHasDependents', value: false });
                    } else {
                      filterDispatch({ type: 'setFilter', name: 'householdHasDependents', value: true });
                    }
                    addSelectedFilterGroup('familyInfo');
                  }}
                />
              </FilterGroupSection>
              <FilterGroupSection label="City">
                <TextField
                  className={classes.textField}
                  onChange={(val) => {
                    filterDispatch({ type: 'setFilter', name: 'city', value: val });
                    addSelectedFilterGroup('familyInfo');
                  }}
                  defaultValue={pendingFilters.city}
                />
              </FilterGroupSection>
              <FilterGroupSection label="State">
                <MetaPickList
                  handleSelect={(v) => {
                    filterDispatch({ type: 'setFilter', name: 'state', value: v.includes(null) ? [] : v });
                    addSelectedFilterGroup('familyInfo');
                  }}
                  options={resource.meta().queries().head()?.field('state')
                    .options()
                    .list()
                    .map(el => el._attributeMap)}
                  value={pendingFilters.state}
                  allowMultiple
                  fullWidth
                />
              </FilterGroupSection>
            </FilterGroupColumn>
          </FilterGroupRow>
        </FilterGroup>
        <FilterGroup
          id={'filter-group-planning-information'}
          groupName="Planning Information"
          className={classes.group}
          test="bulk-communication-filters"
          onApply={handleApplyFilters}
          ApplyFiltersButtonProps={{
            disabled: !selectedFilterGroups.includes('planningInfo'),
          }}
        >
          <FilterGroupRow>
            <FilterGroupColumn>
              {resource.meta().queries().head()?.field('leadPlannerAgentId')
                .options()
                .length() > 0 && (
                <FilterGroupSection label="Lead Planner">
                  <Autocomplete
                    className={classes.autocomplete}
                    options={resource.meta().queries().head()?.field('leadPlannerAgentId')
                      .options()
                      .list()
                      .map(el => ({ name: el.attr('prompt'), value: el.attr('id') }))}
                    onChange={(event) => {
                      filterDispatch({ type: 'setFilter', name: 'leadPlannerAgentId', value: event.target.value || undefined });
                      addSelectedFilterGroup('planningInfo');
                    }}
                    value={pendingFilters.leadPlannerAgentId}
                    SelectProps={{
                      noOptionsText: 'No Lead Planners found 😮',
                    }}
                  />
                </FilterGroupSection>
              )}
              <RangeSlider
                label="Total Household Assets"
                minBound={Number(getMetaOptionMin('householdTotalAssetsUnderManagementMin'))}
                maxBound={Number(getMetaOptionMax('householdTotalAssetsUnderManagementMax'))}
                minValue={Number(pendingFilters.assetTotalMin || getMetaOptionDefault('householdTotalAssetsUnderManagementMin'))}
                maxValue={Number(pendingFilters.assetTotalMax || getMetaOptionDefault('householdTotalAssetsUnderManagementMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'assetTotalMin', value: v });
                  addSelectedFilterGroup('planningInfo');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'assetTotalMax', value: v });
                  addSelectedFilterGroup('planningInfo');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      {'$'}
                    </InputAdornment>),
                }}
              />
              <RangeSlider
                label="Total Household Liabilities"
                minBound={Number(getMetaOptionMin('liabilityTotalMin'))}
                maxBound={Number(getMetaOptionMax('liabilityTotalMax'))}
                minValue={Number(pendingFilters.liabilityTotalMin || getMetaOptionDefault('liabilityTotalMin'))}
                maxValue={Number(pendingFilters.liabilityTotalMax || getMetaOptionDefault('liabilityTotalMax'))}
                onMinUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'liabilityTotalMin', value: v });
                  addSelectedFilterGroup('planningInfo');
                }}
                onMaxUpdate={(v) => {
                  filterDispatch({ type: 'setFilter', name: 'liabilityTotalMax', value: v });
                  addSelectedFilterGroup('planningInfo');
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                    >
                      {'$'}
                    </InputAdornment>),
                }}
              />
              <FilterGroupSection label="Household Liability Types">
                <MetaPickList
                  handleSelect={(v) => {
                    filterDispatch({ type: 'setFilter', name: 'householdLiabilityTypes', value: v.includes(null) ? [] : v });
                    addSelectedFilterGroup('planningInfo');
                  }}
                  options={resource.meta().queries().head()?.field('householdLiabilityTypes')
                    .options()
                    .list()
                    .map(el => el._attributeMap)}
                  value={pendingFilters.householdLiabilityTypes}
                  allowMultiple
                  fullWidth
                />
              </FilterGroupSection>
            </FilterGroupColumn>
          </FilterGroupRow>
        </FilterGroup>
        <Spacer />
      </div>
      <BulkCommunicationSelectedFilters
        resetSelectedFilterGroups={() => setSelectedFilterGroups([])}
        onApplyFilters={onApplyFilters}
      />
    </div>
  );
};

BulkCommunicationFilters.defaultProps = {
  displayNeedsPlanningMeetingFilter: false,
  linkUrl: 'bulkCommunicationLink',
  queryRel: 'getBulkCommunications',
  onApplyFilters: null,
};

BulkCommunicationFilters.propTypes = {
  displayNeedsPlanningMeetingFilter: PropTypes.bool,
  linkUrl: PropTypes.string,
  queryRel: PropTypes.string,
  onApplyFilters: PropTypes.func,
  resource: PropTypes.object.isRequired,
};

export default BulkCommunicationFilters;
